import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import {
  type GetUserSettingsResponse,
  type UpdateProfileSettingsRequest,
  UserProfileApi,
} from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

interface IUseUpdateCommunicationSettings extends UpdateProfileSettingsRequest {
  userId: number;
}

export const useUpdateCommunicationSettings = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isSuccess, isError, isPending } = useMutation({
    mutationFn: async ({
      userId,
      sendCommunicationalSms,
      sendTransactionalSms,
      sendPromotionalSms,
      allowAutoPayment,
    }: IUseUpdateCommunicationSettings) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(UserProfileApi, accessToken);
      return service.apiUserProfileIdSettingsPut({
        id: userId,
        updateProfileSettingsRequest: {
          sendCommunicationalSms,
          sendPromotionalSms,
          sendTransactionalSms,
          allowAutoPayment,
        },
      });
    },
    onSuccess: async (_data, variables) => {
      toast({
        title: "Communication settings updated",
        description: "Communication settings updated successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      // Update cache with saved values so when user switches tab and comes back they see correct checkboxes.
      // Do NOT invalidateQueries here: refetch would overwrite cache with server response; if server
      // returns stale data we'd show all unchecked again.
      const updatedSettings: GetUserSettingsResponse = {
        userId: variables.userId,
        sendPromotionalSms: variables.sendPromotionalSms,
        sendTransactionalSms: variables.sendTransactionalSms,
        sendCommunicationalSms: variables.sendCommunicationalSms,
        allowAutoPayment: variables.allowAutoPayment,
      };
      queryClient.setQueryData(
        [QueryKeys.CommunicationSettings, variables.userId],
        updatedSettings,
      );
    },
    onError: (error: any, variables) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update communication settings";
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isSuccess, isError, isPending };
};
